/**
 * Estate and run panels for the internal AEOS operations surface.
 *
 * The presentation rules here are not stylistic. Each one exists because the
 * underlying AEOS data invites a wrong conclusion:
 *
 * - Both `reportedStatus` and `derivedStatus` are shown, because Peregrine ages a
 *   stuck `in_progress` row to `abandoned` and hiding either value would either
 *   overstate AEOS or contradict it.
 * - Counters that AEOS bumps on connectivity tests are quarantined in their own
 *   labelled area. Merged into freshness they would read as sync history.
 * - Open runs render "not available while running" rather than `0`, because AEOS
 *   leaves those columns at zero until a run ends.
 * - Logical syncs are labelled inferred, because AEOS emits no correlation id.
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  ColumnLayout,
  Container,
  ExpandableSection,
  Header,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
  type SelectProps,
  type StatusIndicatorProps,
  type TableProps,
} from '@cloudscape-design/components';

import {
  AEOS_RUN_STATUSES,
  listAeosRunEntities,
  listAeosRuns,
  type AeosCoverage,
  type AeosDerivedRunStatus,
  type AeosEstateReport,
  type AeosIntegrationRow,
  type AeosLogicalSync,
  type AeosRunEntityPage,
  type AeosRunFeed,
  type AeosRunRow,
  type AeosRunStatus,
} from '../../api/aeos';

const PROGRESS_UNAVAILABLE = 'not available while running';
const NO_CORRELATION_ID_NOTE =
  'AEOS emits no correlation id for a sync. Membership below is inferred from timing proximity within a single partition, so one sync can be split into several rows and two unrelated syncs can be merged into one.';
const ORIGIN_NOTE =
  'AEOS writes sync_type as manually triggered on every run, so origin is derived from the partition key: cron activity lands under scheduled-{provider}.';
const OPEN_RUN_NOTE =
  'records_processed, records_failed and duration_ms stay at 0 in AEOS until a run ends. While a run is open — including one aged to abandoned — those columns are withheld rather than shown as zero progress.';

/* ------------------------------------------------------------------ */
/* Limitations and validation notices                                  */
/* ------------------------------------------------------------------ */

interface AeosLimitationsNoticeProps {
  headerText: string;
  represented: readonly string[];
  notRepresented: readonly string[];
  sourceNote: string;
}

/**
 * Collapsed, never hidden. An operator who does not open it still sees that this
 * view is partial; one who opens it sees exactly where the gaps are.
 */
export function AeosLimitationsNotice({
  headerText,
  represented,
  notRepresented,
  sourceNote,
}: AeosLimitationsNoticeProps): ReactElement {
  return (
    <ExpandableSection variant="container" defaultExpanded={false} headerText={headerText}>
      <SpaceBetween size="m">
        <Box variant="p">{sourceNote}</Box>
        <div>
          <Box variant="awsui-key-label">Not represented — read nothing into its absence</Box>
          <ul>
            {notRepresented.map((entry) => (
              <li key={entry}>{entry}</li>
            ))}
          </ul>
        </div>
        <div>
          <Box variant="awsui-key-label">Represented</Box>
          <ul>
            {represented.map((entry) => (
              <li key={entry}>{entry}</li>
            ))}
          </ul>
        </div>
      </SpaceBetween>
    </ExpandableSection>
  );
}

interface AeosCoverageNoticeProps {
  coverage: AeosCoverage;
  surfaceLabel: string;
}

export function AeosCoverageNotice({
  coverage,
  surfaceLabel,
}: AeosCoverageNoticeProps): ReactElement {
  return (
    <AeosLimitationsNotice
      headerText={`Limitations of this view — ${surfaceLabel} coverage is ${coverage.status}`}
      represented={coverage.represented}
      notRepresented={coverage.notRepresented}
      sourceNote="The API returns this coverage statement with every response. It is partial by design, not by fault."
    />
  );
}

interface AeosInvalidItemsAlertProps {
  invalidItemCount: number;
  subject: string;
}

/** A non-zero count means rows failed validation, so this view is missing data. */
export function AeosInvalidItemsAlert({
  invalidItemCount,
  subject,
}: AeosInvalidItemsAlertProps): ReactElement | null {
  if (invalidItemCount <= 0) {
    return null;
  }
  return (
    <Alert
      type="warning"
      header={`${invalidItemCount} ${subject} record${invalidItemCount === 1 ? '' : 's'} failed validation and is missing`}
    >
      Rows that did not match the expected AEOS shape were dropped before this view was
      built. Counts, freshness and totals below are therefore incomplete — treat an empty
      or low value as unknown, not as zero.
    </Alert>
  );
}

interface AeosScopeBadgeProps {
  scope: 'global' | 'account';
}

/**
 * Scope, stated in words on every row that has one.
 *
 * AEOS keys module settings and the invoice policy as single rows with no account
 * dimension, so "global" here means every customer account at once. The badge spells
 * that out rather than relying on colour, which conveys nothing on its own.
 */
export function AeosScopeBadge({ scope }: AeosScopeBadgeProps): ReactElement {
  return (
    <Badge color={scope === 'global' ? 'red' : 'blue'}>
      {scope === 'global' ? 'GLOBAL — affects every account' : 'account'}
    </Badge>
  );
}

/* ------------------------------------------------------------------ */
/* Formatting                                                          */
/* ------------------------------------------------------------------ */

/** Raw AEOS strings, unreformatted: some carry no UTC offset, so localizing lies. */
function formatInstant(value: string | undefined): string {
  return value === undefined || value.trim().length === 0 ? '—' : value;
}

function formatCount(value: number | undefined): string {
  return value === undefined ? '—' : String(value);
}

function formatDuration(milliseconds: number | undefined): string {
  if (milliseconds === undefined) {
    return '—';
  }
  if (milliseconds < 1000) {
    return `${milliseconds} ms`;
  }
  const seconds = milliseconds / 1000;
  return seconds < 120 ? `${seconds.toFixed(1)} s` : `${Math.round(seconds / 60)} min`;
}

function derivedStatusType(status: AeosDerivedRunStatus): StatusIndicatorProps.Type {
  switch (status) {
    case 'succeeded':
      return 'success';
    case 'partial_success':
      return 'warning';
    case 'failed':
      return 'error';
    case 'in_progress':
      return 'in-progress';
    case 'abandoned':
      return 'error';
  }
}

interface RunStatusPairProps {
  run: AeosRunRow;
}

/** Both values, side by side, each labelled with whose claim it is. */
function ReportedStatusCell({ run }: RunStatusPairProps): ReactElement {
  return (
    <SpaceBetween size="xxs">
      <Box variant="span">{run.reportedStatus}</Box>
      <Box variant="small">as AEOS wrote it</Box>
    </SpaceBetween>
  );
}

function DerivedStatusCell({ run }: RunStatusPairProps): ReactElement {
  return (
    <SpaceBetween size="xxs">
      <StatusIndicator type={derivedStatusType(run.derivedStatus)}>
        {run.derivedStatus}
      </StatusIndicator>
      {run.derivedStatus === 'abandoned' ? (
        <Box variant="small">
          open for {formatDuration(run.openForMs)} and past the staleness threshold — AEOS
          still reports it as in_progress
        </Box>
      ) : (
        <Box variant="small">Peregrine reading; adds abandoned</Box>
      )}
    </SpaceBetween>
  );
}

function ProgressCell({ run }: RunStatusPairProps): ReactElement {
  if (!run.progressAvailable) {
    return <Box variant="span">{PROGRESS_UNAVAILABLE}</Box>;
  }
  return (
    <Box variant="span">
      {formatCount(run.recordsProcessed)} processed / {formatCount(run.recordsFailed)} failed
    </Box>
  );
}

function DurationCell({ run }: RunStatusPairProps): ReactElement {
  return (
    <Box variant="span">
      {run.progressAvailable ? formatDuration(run.durationMs) : PROGRESS_UNAVAILABLE}
    </Box>
  );
}

function OriginCell({ run }: RunStatusPairProps): ReactElement {
  return (
    <SpaceBetween size="xxs">
      <Badge color={run.origin === 'scheduled' ? 'blue' : 'grey'}>{run.origin}</Badge>
      <Box variant="small">partition {run.partitionIntegrationId}</Box>
    </SpaceBetween>
  );
}

function runColumnDefinitions(): TableProps.ColumnDefinition<AeosRunRow>[] {
  return [
    { id: 'syncId', header: 'Sync id', cell: (run) => <code>{run.syncId}</code> },
    { id: 'origin', header: 'Origin', cell: (run) => <OriginCell run={run} /> },
    { id: 'started', header: 'Sync timestamp', cell: (run) => formatInstant(run.syncTimestamp) },
    {
      id: 'reported',
      header: 'Reported status',
      cell: (run) => <ReportedStatusCell run={run} />,
    },
    { id: 'derived', header: 'Derived status', cell: (run) => <DerivedStatusCell run={run} /> },
    { id: 'records', header: 'Records', cell: (run) => <ProgressCell run={run} /> },
    { id: 'duration', header: 'Duration', cell: (run) => <DurationCell run={run} /> },
    { id: 'error', header: 'Error summary', cell: (run) => run.errorSummary ?? '—' },
  ];
}

/* ------------------------------------------------------------------ */
/* Run table                                                           */
/* ------------------------------------------------------------------ */

interface AeosRunTableProps {
  runs: AeosRunRow[];
  isLoading: boolean;
  headerText: string;
  emptyText: string;
  selectedSyncId?: string;
  onSelectRun?: (run: AeosRunRow | undefined) => void;
}

export function AeosRunTable({
  runs,
  isLoading,
  headerText,
  emptyText,
  selectedSyncId,
  onSelectRun,
}: AeosRunTableProps): ReactElement {
  const isSelectable = onSelectRun !== undefined;
  return (
    <Table
      variant="embedded"
      loading={isLoading}
      loadingText="Reading AEOS run history"
      items={runs}
      trackBy="syncId"
      {...(isSelectable
        ? {
            selectionType: 'single' as const,
            selectedItems: runs.filter((run) => run.syncId === selectedSyncId),
            onSelectionChange: ({ detail }: { detail: { selectedItems: AeosRunRow[] } }) =>
              onSelectRun(detail.selectedItems[0]),
          }
        : {})}
      header={
        <Header variant="h3" counter={`(${runs.length})`} description={OPEN_RUN_NOTE}>
          {headerText}
        </Header>
      }
      empty={<Box textAlign="center">{emptyText}</Box>}
      columnDefinitions={runColumnDefinitions()}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Estate panel                                                        */
/* ------------------------------------------------------------------ */

interface AeosEstatePanelProps {
  isLoading: boolean;
  report: AeosEstateReport | undefined;
  selectedIntegrationId: string | undefined;
  onSelectIntegration: (integrationId: string | undefined) => void;
}

export function AeosEstatePanel({
  isLoading,
  report,
  selectedIntegrationId,
  onSelectIntegration,
}: AeosEstatePanelProps): ReactElement {
  const integrations = report?.integrations ?? [];
  const selected = integrations.find(
    (integration) => integration.integrationId === selectedIntegrationId,
  );

  return (
    <SpaceBetween size="l">
      <AeosInvalidItemsAlert
        invalidItemCount={report?.invalidItemCount ?? 0}
        subject="integration or run"
      />
      {report ? (
        <AeosCoverageNotice coverage={report.coverage} surfaceLabel="estate" />
      ) : null}
      <IntegrationTable
        isLoading={isLoading}
        integrations={integrations}
        selectedIntegrationId={selectedIntegrationId}
        onSelectIntegration={onSelectIntegration}
      />
      {selected ? (
        <IntegrationDetail integration={selected} />
      ) : (
        <Box textAlign="center" padding="l">
          Select an integration to see its derived freshness, inferred logical syncs and
          run history.
        </Box>
      )}
    </SpaceBetween>
  );
}

interface IntegrationTableProps {
  isLoading: boolean;
  integrations: AeosIntegrationRow[];
  selectedIntegrationId: string | undefined;
  onSelectIntegration: (integrationId: string | undefined) => void;
}

function IntegrationTable({
  isLoading,
  integrations,
  selectedIntegrationId,
  onSelectIntegration,
}: IntegrationTableProps): ReactElement {
  return (
    <Table
      variant="container"
      loading={isLoading}
      loadingText="Reading the AEOS integration estate"
      items={integrations}
      trackBy="integrationId"
      selectionType="single"
      selectedItems={integrations.filter(
        (integration) => integration.integrationId === selectedIntegrationId,
      )}
      onSelectionChange={({ detail }) =>
        onSelectIntegration(detail.selectedItems[0]?.integrationId)
      }
      header={
        <Header
          variant="h2"
          counter={`(${integrations.length})`}
          description="Providers without an AEOS ingest pipeline have no partition to enumerate and are absent from this list entirely."
        >
          AEOS integrations
        </Header>
      }
      empty={<Box textAlign="center">No AEOS integrations were returned.</Box>}
      columnDefinitions={[
        {
          id: 'integrationId',
          header: 'Integration',
          cell: (integration) => <code>{integration.integrationId}</code>,
        },
        { id: 'type', header: 'Type', cell: (integration) => integration.integrationType },
        { id: 'status', header: 'Status', cell: (integration) => integration.status ?? 'unknown' },
        {
          id: 'auth',
          header: 'Auth status',
          cell: (integration) => integration.authStatus ?? 'unknown',
        },
        {
          id: 'enabled',
          header: 'Enabled',
          cell: (integration) =>
            integration.enabled === undefined ? (
              'unknown'
            ) : (
              <StatusIndicator type={integration.enabled ? 'success' : 'stopped'}>
                {integration.enabled ? 'enabled' : 'disabled'}
              </StatusIndicator>
            ),
        },
        {
          id: 'lastRun',
          header: 'Last run (from run history)',
          cell: (integration) => formatInstant(integration.lastRunAt),
        },
        {
          id: 'lastSuccess',
          header: 'Last success (from run history)',
          cell: (integration) => formatInstant(integration.lastSuccessfulRunAt),
        },
        {
          id: 'window',
          header: 'Runs examined',
          cell: (integration) =>
            `${integration.runsExamined} of up to ${integration.runWindowLimit} per partition`,
        },
      ]}
    />
  );
}

interface IntegrationDetailProps {
  integration: AeosIntegrationRow;
}

function IntegrationDetail({ integration }: IntegrationDetailProps): ReactElement {
  return (
    <SpaceBetween size="l">
      <AeosInvalidItemsAlert
        invalidItemCount={integration.invalidItemCount}
        subject="run history"
      />
      <DerivedFreshnessPanel integration={integration} />
      <TestTouchedCountersPanel integration={integration} />
      <LogicalSyncPanel integration={integration} />
      <Container header={<Header variant="h2">Run history</Header>}>
        <Box variant="p" padding={{ bottom: 's' }}>
          {ORIGIN_NOTE}
        </Box>
        <AeosRunTable
          runs={integration.runs}
          isLoading={false}
          headerText="Runs, newest first"
          emptyText="No runs inside the examined window."
        />
      </Container>
    </SpaceBetween>
  );
}

function DerivedFreshnessPanel({ integration }: IntegrationDetailProps): ReactElement {
  const { runCounts } = integration;
  return (
    <Container
      header={
        <Header
          variant="h2"
          description={`Derived from the run-history table. Freshness and counts describe the most recent ${integration.runWindowLimit} runs per partition — ${integration.runsExamined} were examined — and are not lifetime totals for this integration.`}
        >
          Sync freshness and run outcomes (derived from run history)
        </Header>
      }
    >
      <ColumnLayout columns={4} variant="text-grid">
        <div>
          <Box variant="awsui-key-label">Last run at</Box>
          <Box>{formatInstant(integration.lastRunAt)}</Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Last successful run at</Box>
          <Box>{formatInstant(integration.lastSuccessfulRunAt)}</Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Runs examined</Box>
          <Box>{integration.runsExamined}</Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Run window limit</Box>
          <Box>{integration.runWindowLimit} per partition</Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Succeeded</Box>
          <Box>{runCounts.succeeded}</Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Partial success</Box>
          <Box>{runCounts.partialSuccess}</Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Failed</Box>
          <Box>{runCounts.failed}</Box>
        </div>
        <div>
          <Box variant="awsui-key-label">In progress / abandoned</Box>
          <Box>
            {runCounts.inProgress} in progress, {runCounts.abandoned} abandoned
          </Box>
        </div>
      </ColumnLayout>
    </Container>
  );
}

/**
 * Deliberately its own container, away from the derived counts above.
 *
 * AEOS increments these on connectivity tests as well as on syncs, so a healthy
 * `successfulSyncs` can mean nothing more than that somebody pressed "test
 * connection". They are shown because an operator will look for them, and separated
 * because merging them into freshness would launder a test into a sync.
 */
function TestTouchedCountersPanel({ integration }: IntegrationDetailProps): ReactElement {
  const counters = integration.testTouchedCounters;
  return (
    <Container
      header={
        <Header variant="h2">
          Integration record counters — bumped by connectivity tests, not sync facts
        </Header>
      }
    >
      <SpaceBetween size="m">
        <Alert type="info" header="These are not sync counts">
          AEOS increments <code>last_sync_at</code>, <code>total_syncs</code>,{' '}
          <code>successful_syncs</code>, <code>failed_syncs</code> and{' '}
          <code>consecutive_failures</code> on connectivity tests as well as on syncs. A
          value here can be entirely explained by someone testing the connection, so it
          says nothing about whether data moved. Use the derived freshness and run counts
          above for sync questions.
        </Alert>
        <ColumnLayout columns={5} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">last_sync_at</Box>
            <Box>{formatInstant(counters.lastSyncAt)}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">total_syncs</Box>
            <Box>{formatCount(counters.totalSyncs)}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">successful_syncs</Box>
            <Box>{formatCount(counters.successfulSyncs)}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">failed_syncs</Box>
            <Box>{formatCount(counters.failedSyncs)}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">consecutive_failures</Box>
            <Box>{formatCount(counters.consecutiveFailures)}</Box>
          </div>
        </ColumnLayout>
      </SpaceBetween>
    </Container>
  );
}

function LogicalSyncPanel({ integration }: IntegrationDetailProps): ReactElement {
  return (
    <Container
      header={
        <Header variant="h2" counter={`(${integration.logicalSyncs.length})`}>
          Logical syncs — inferred, not reported by AEOS
        </Header>
      }
    >
      <SpaceBetween size="m">
        <Alert type="info" header="Grouping is inferred from timing proximity">
          {NO_CORRELATION_ID_NOTE} One sync fans out to a run per entity type plus one per
          continuation, each with its own id, so member counts are a best reading rather
          than a fact.
        </Alert>
        <Table
          variant="embedded"
          items={integration.logicalSyncs}
          trackBy={(sync) => `${sync.partitionIntegrationId}#${sync.windowStart}`}
          empty={<Box textAlign="center">No logical syncs inside the examined window.</Box>}
          columnDefinitions={logicalSyncColumnDefinitions()}
        />
      </SpaceBetween>
    </Container>
  );
}

function logicalSyncColumnDefinitions(): TableProps.ColumnDefinition<AeosLogicalSync>[] {
  return [
    {
      id: 'correlation',
      header: 'Correlation',
      cell: (sync) => <Badge color="grey">{sync.correlation}</Badge>,
    },
    {
      id: 'origin',
      header: 'Origin',
      cell: (sync) => `${sync.origin} (partition ${sync.partitionIntegrationId})`,
    },
    { id: 'windowStart', header: 'Window start', cell: (sync) => formatInstant(sync.windowStart) },
    { id: 'windowEnd', header: 'Window end', cell: (sync) => formatInstant(sync.windowEnd) },
    {
      id: 'members',
      header: 'Member runs (inferred)',
      cell: (sync) => `${sync.memberRunCount} run${sync.memberRunCount === 1 ? '' : 's'}`,
    },
    {
      id: 'status',
      header: 'Derived status',
      cell: (sync) => (
        <StatusIndicator type={derivedStatusType(sync.derivedStatus)}>
          {sync.derivedStatus}
        </StatusIndicator>
      ),
    },
    {
      id: 'records',
      header: 'Records',
      cell: (sync) =>
        sync.progressAvailable
          ? `${formatCount(sync.recordsProcessed)} processed / ${formatCount(sync.recordsFailed)} failed`
          : PROGRESS_UNAVAILABLE,
    },
  ];
}

/* ------------------------------------------------------------------ */
/* Runs panel                                                          */
/* ------------------------------------------------------------------ */

const RUN_LIMIT_OPTIONS: SelectProps.Option[] = [
  { value: '25', label: '25 runs' },
  { value: '50', label: '50 runs' },
  { value: '100', label: '100 runs' },
];

function statusOption(status: AeosRunStatus): SelectProps.Option {
  return { value: status, label: status };
}

interface AeosRunsPanelProps {
  /** Bumped by the page's refresh action so the feed reloads with everything else. */
  reloadToken: number;
}

export function AeosRunsPanel({ reloadToken }: AeosRunsPanelProps): ReactElement {
  const [status, setStatus] = useState<AeosRunStatus>('in_progress');
  const [limit, setLimit] = useState('50');
  const [feed, setFeed] = useState<AeosRunFeed | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [selectedRun, setSelectedRun] = useState<AeosRunRow | undefined>();

  useEffect(() => {
    let isCancelled = false;
    setIsLoading(true);
    setError(undefined);
    void listAeosRuns(status, Number(limit))
      .then((response) => {
        if (!isCancelled) {
          setFeed(response);
          setSelectedRun(undefined);
        }
      })
      .catch((feedError: unknown) => {
        if (!isCancelled) {
          setError(feedError instanceof Error ? feedError.message : 'Unable to read the run feed');
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      isCancelled = true;
    };
  }, [status, limit, reloadToken]);

  return (
    <SpaceBetween size="l">
      {error ? (
        <Alert type="error" header="Run feed request failed">
          {error}
        </Alert>
      ) : null}
      <Alert type="info" header="The filter is the status AEOS wrote, not the derived status">
        AEOS is queried by <code>reportedStatus</code>. There is no <code>abandoned</code>{' '}
        partition to query, so runs Peregrine reads as abandoned appear under{' '}
        <code>in_progress</code>. Scheduled rows carry the{' '}
        <code>scheduled-&#123;provider&#125;</code> partition as their integration id, and
        the real integration is not resolvable from the row alone.
      </Alert>
      <AeosInvalidItemsAlert invalidItemCount={feed?.invalidItemCount ?? 0} subject="run" />
      {feed ? <AeosCoverageNotice coverage={feed.coverage} surfaceLabel="run feed" /> : null}
      <Container
        header={
          <Header
            variant="h2"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Select
                  selectedOption={statusOption(status)}
                  options={AEOS_RUN_STATUSES.map(statusOption)}
                  ariaLabel="Reported run status to query"
                  onChange={({ detail }) => {
                    const next = AEOS_RUN_STATUSES.find(
                      (candidate) => candidate === detail.selectedOption.value,
                    );
                    if (next) {
                      setStatus(next);
                    }
                  }}
                />
                <Select
                  selectedOption={
                    RUN_LIMIT_OPTIONS.find((option) => option.value === limit) ?? null
                  }
                  options={RUN_LIMIT_OPTIONS}
                  ariaLabel="Maximum runs to read"
                  onChange={({ detail }) => setLimit(detail.selectedOption.value ?? '50')}
                />
              </SpaceBetween>
            }
          >
            Fleet-wide runs reported as {status}
          </Header>
        }
      >
        <AeosRunTable
          runs={feed?.runs ?? []}
          isLoading={isLoading}
          headerText="Runs, newest first"
          emptyText="No runs under this reported status."
          selectedSyncId={selectedRun?.syncId}
          onSelectRun={setSelectedRun}
        />
      </Container>
      <RunEntityPanel run={selectedRun} />
    </SpaceBetween>
  );
}

interface RunEntityPanelProps {
  run: AeosRunRow | undefined;
}

function RunEntityPanel({ run }: RunEntityPanelProps): ReactElement {
  const [page, setPage] = useState<AeosRunEntityPage | undefined>();
  const [rows, setRows] = useState<AeosRunEntityPage['entities']>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    setPage(undefined);
    setRows([]);
    setError(undefined);
    if (!run) {
      return;
    }
    let isCancelled = false;
    setIsLoading(true);
    void listAeosRunEntities(run.syncId, 50)
      .then((response) => {
        if (!isCancelled) {
          setPage(response);
          setRows(response.entities);
        }
      })
      .catch((entityError: unknown) => {
        if (!isCancelled) {
          setError(
            entityError instanceof Error ? entityError.message : 'Unable to read run records',
          );
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      isCancelled = true;
    };
  }, [run]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (!run || page?.nextCursor === undefined) {
      return;
    }
    setIsLoading(true);
    try {
      const next = await listAeosRunEntities(run.syncId, 50, page.nextCursor);
      setPage(next);
      setRows((current) => [...current, ...next.entities]);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Unable to read more records');
    } finally {
      setIsLoading(false);
    }
  }, [run, page]);

  if (!run) {
    return (
      <Box textAlign="center" padding="l">
        Select a run to read its per-record entity rows.
      </Box>
    );
  }

  return (
    <Container
      header={
        <Header
          variant="h2"
          counter={`(${rows.length})`}
          description="Entity rows exist only for runs that reached the record-processing stage. Before/after CRM payloads are customer data and are never read."
          actions={
            <Button
              disabled={page?.nextCursor === undefined}
              loading={isLoading}
              onClick={() => void loadMore()}
            >
              Load more records
            </Button>
          }
        >
          Records for sync {run.syncId}
        </Header>
      }
    >
      <SpaceBetween size="m">
        {error ? (
          <Alert type="error" header="Record request failed">
            {error}
          </Alert>
        ) : null}
        <AeosInvalidItemsAlert
          invalidItemCount={page?.invalidItemCount ?? 0}
          subject="entity"
        />
        {page ? (
          <AeosCoverageNotice coverage={page.coverage} surfaceLabel="run records" />
        ) : null}
        <Table
          variant="embedded"
          loading={isLoading}
          loadingText="Reading run records"
          items={rows}
          trackBy={(entity) => `${entity.entityType}#${entity.entityId}`}
          empty={
            <Box textAlign="center">
              No entity rows. This run may not have reached record processing.
            </Box>
          }
          columnDefinitions={[
            { id: 'entityType', header: 'Entity type', cell: (entity) => entity.entityType },
            {
              id: 'entityId',
              header: 'Entity id',
              cell: (entity) => <code>{entity.entityId}</code>,
            },
            { id: 'internalId', header: 'Internal id', cell: (entity) => entity.internalId ?? '—' },
            { id: 'operation', header: 'Operation', cell: (entity) => entity.operation ?? '—' },
            { id: 'status', header: 'Status', cell: (entity) => entity.status },
            {
              id: 'processedAt',
              header: 'Processed at',
              cell: (entity) => formatInstant(entity.processedAt),
            },
            {
              id: 'error',
              header: 'Error',
              cell: (entity) =>
                entity.errorCode === undefined && entity.errorMessage === undefined
                  ? '—'
                  : `${entity.errorCode ?? 'no code'}: ${entity.errorMessage ?? 'no message'}`,
            },
          ]}
        />
      </SpaceBetween>
    </Container>
  );
}
